import { useEffect } from 'react'
import useTypedSelector from 'interfaces/useTypedSelector'
import { useRouter } from 'next/router'
import { ActiveFilters, setUrlParamActiveFilters, setUrlParamsStatus } from 'reducers/filtersReducer'
import { useDispatch } from 'react-redux'
import {
	activeFiltersFromReadableUrlParams,
	readableFiltersFromActiveFilters,
} from 'helpers/filterHelpers/filtersUrlHelper'
import { setProductsPage } from 'reducers/productReducer'

// Hook that populates query url according activeFilters and page selected on Filtered Product List
const useFiltersUrlQueryParams = () => {
	const { activeFilters, urlParamsStatus, filters } = useTypedSelector(({ filters }) => filters)
	const { page } = useTypedSelector(({ products }) => products).filteredProductList
	const dispatch = useDispatch()
	const { query, ...router } = useRouter()
	const urlQueryDelimiter = ';'

	const parseFilters = (search: string) => {
		const searchParams = new URLSearchParams(search)
		const urlParamFilters: ActiveFilters = activeFiltersFromReadableUrlParams(searchParams, urlQueryDelimiter, filters)
		return urlParamFilters
	}

	const parsePage = (search: string) => {
		const searchParams = new URLSearchParams(search)
		return parseInt(searchParams.get('page') ?? '1')
	}

	const setUrlParams = () => {
		const search = location.search

		// If there are not url params we flag them set and return
		if (search.length === 0) {
			dispatch(setUrlParamsStatus('set'))
			return
		}

		// If there are url params present we gather them and dispatch them to the store
		const urlParamFilters = parseFilters(search)
		const page = parsePage(search)

		dispatch(setUrlParamActiveFilters(urlParamFilters))
		dispatch(setProductsPage(page))
	}

	const compareArray = (arr1: string[], arr2: string[]) => {
		return JSON.stringify(arr1) === JSON.stringify(arr2)
	}

	const filtersHaveChanged = () => {
		let changed = false
		const search = location.search
		const currentFilters = parseFilters(search)
		const currentPage = parsePage(search)

		if (activeFilters.sort !== currentFilters.sort) changed = true
		else if (activeFilters.search !== currentFilters.search) changed = true
		else if (!compareArray(activeFilters.allergenFilterIds, currentFilters.allergenFilterIds)) changed = true
		else if (!compareArray(activeFilters.catTreeFilterIds, currentFilters.catTreeFilterIds)) changed = true
		else if (!compareArray(activeFilters.catTreePath, currentFilters.catTreePath)) changed = true
		else if (page !== currentPage) changed = true
		return changed
	}

	const constructPath = (page: number) => {
		// @ts-ignore
		const readableFilters = readableFiltersFromActiveFilters(activeFilters, filters)
		// @ts-ignore
		const params = new URLSearchParams(readableFilters)
		params.append('page', page.toString())

		const emptyKeys: string[] = []
		params.forEach((param: string, key: string) => {
			if (!param || param === '') {
				emptyKeys.push(key)
			}
		})
		// remove keys with no values for cleaner url
		emptyKeys.forEach(x => params.delete(x))
		return emptyKeys.length === Object.keys(activeFilters).length + 1
			? '/vorur'
			: `/vorur?${params
					.toString()
					.split('%2C') // comma
					.join(urlQueryDelimiter)}`
	}

	useEffect(() => {
		// If url params have not been set up in the store, we gather them and
		// set them in the store
		if (urlParamsStatus === 'not-set') {
			setUrlParams()
		} else if (filtersHaveChanged()) {
			// If the url params have been set we are reacting to them changing and update the url
			// only if we haven't updated the filters on query param change
			// @ts-ignore
			router.push(constructPath(page), undefined, { shallow: true })
		}
	}, [activeFilters, page])

	useEffect(() => {
		// If the filters have changed externally we update them here
		const changed = filtersHaveChanged()
		if (changed) {
			setUrlParams()
		}
	}, [query])

	return {
		nextPagePath: constructPath(page + 1),
		previousPagePath: constructPath(page - 1),
	}
}

export default useFiltersUrlQueryParams
